import { useMutation } from '@tanstack/react-query';
import { AvField, AvForm, AvGroup } from 'availity-reactstrap-validation';
import React, { useEffect, useState } from 'react';
import OtherAssetsAttachmentsService from "src/modules/data-protection/apis/OtherAssetsAttachmentsService";
import { withNamespaces } from 'react-i18next';
import {
    Modal, 
    ModalHeader, 
    ModalBody, 
    ModalFooter,
    Button, 
    FormGroup, 
    Label,
    Input,
    Spinner
} from 'reactstrap';
import { toast } from 'react-toastify';
import { DocumentsCategoryTypes, DocumentsConfidentialityLevels, DocumentsTypes } from 'src/modules/data-protection/constants';

const AddAttachmentModal = ({ 
    t, 
    isOpen, 
    toggle, 
    onComplete,
    assetId
}) => {
    const [contentType, setContentType] = useState('file');
    const [file, setFile] = useState(null);

    const {
        mutate: addAttachmentMutation,
        isLoading: addingAttachmentInProcess
    } = useMutation({
        mutationFn: async (payload) => {
            const service = OtherAssetsAttachmentsService.getInstance();
            return await service.addAttachment(assetId, payload);
        },
        onSuccess: () => {
            toast(t("File uploaded successfully"), {
                type: "success"
            });
            onComplete && onComplete();
            toggle();
        },
        onError: () => {
            toast(t("An error occurred while uploading the file"), {
                type: "error"
            });
        }
    });

    const handleSubmit = (e, values) => {
        e.preventDefault();
        addAttachmentMutation({
            ...values,
            file: contentType === 'file' ? file : undefined
        });
    };

    useEffect(() => {
        if(isOpen){
            setFile(null);
            setContentType('file');
        }
    }, [ isOpen ]);

    return (
        <Modal isOpen={isOpen} toggle={toggle}>
            <ModalHeader toggle={toggle}>
                {t('Attach Your File')}
            </ModalHeader>
            <AvForm 
                onValidSubmit={handleSubmit} 
                className="needs-validation"
            >
                <ModalBody>
                    <AvGroup className='mb-3'>
                        <Label for="title">{t('Title')}</Label>
                        <AvField
                            id="title"
                            name="title"
                            type="text"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: true }
                            }}
                        />
                    </AvGroup>

                    <AvGroup className='mb-3'>
                        <Label for="description">{t('Description')}</Label>
                        <AvField
                            id="description"
                            name="description"
                            type="textarea"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: false }
                            }}
                        />
                    </AvGroup>

                    <AvGroup className='mb-3'>
                        <Label for="contentType">
                            {t('Content Type')}
                        </Label>
                        
                        <AvField
                            id="contentType"
                            name="contentType"
                            type="select"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: true }
                            }}
                            onChange={(e) => setContentType(e.target.value)}
                            value={contentType}
                        >
                            <option value='file'>
                                {t('File')}
                            </option>

                            <option value='link'>
                                {t('Link')}
                            </option>
                        </AvField>
                    </AvGroup>

                    {contentType === 'file' ? (
                        <FormGroup>
                            <Label for="file">
                                {t('File Upload')}
                            </Label>
                            <div
                                className='d-flex align-items-center'
                            >
                                <Input
                                    type="file"
                                    name="file"
                                    id="file"
                                    required
                                    onChange={(e) => {
                                        setFile(e.target.files[0])
                                    }}
                                    className='d-none'
                                />
                                <label
                                    htmlFor="file"
                                    className="btn btn-outline-primary me-2"
                                >
                                    {t('Choose File')}
                                </label>
                                {file && <small className="text-muted">{file.name}</small>}
                            </div>
                            <AvField
                                id="file"
                                name="file"
                                type="hidden"
                                validate={{
                                    required: { value: true }
                                }}
                                errorMessage={t("This field cannot be blank")}
                                value={file?.name}
                            />
                        </FormGroup>
                    ) : (
                        <AvGroup className='mb-3'>
                            <Label for="path">{t('Path')}</Label>
                            <AvField
                                id="path"
                                name="path"
                                type="text"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: true },
                                    pattern: {
                                        value: /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/,
                                        errorMessage: t("Please enter a valid URL"),
                                    }
                                }}
                            />
                        </AvGroup>
                    )}

                    <AvGroup className='mb-3'>
                        <Label for="type">{t('Type')}</Label>
                        <AvField
                            id="type"
                            name="type"
                            type="select"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: true }
                            }}
                            defaultValue='internal'
                        >
                            {DocumentsTypes.filter((documentType) => documentType.creatable).map((documentType) => {
                                return (
                                    <option value={documentType.value}>
                                        {t(documentType.title)}
                                    </option>
                                )
                            })}
                        </AvField>
                    </AvGroup>

                    <AvGroup className='mb-3'>
                        <Label for="category">{t('Category')}</Label>
                        <AvField
                            id="category"
                            name="category"
                            type="select"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: true }
                            }}
                        >
                            <option value="">-- {t('Select Category')} --</option>

                            {DocumentsCategoryTypes.map((categoryType) => {
                                return (
                                    <option value={categoryType.value}>
                                        {t(categoryType.title)}
                                    </option>
                                )
                            })}
                        </AvField>
                    </AvGroup>

                    <AvGroup className='mb-3'>
                        <Label for="confidentialityLevel">{t('Confidentiality Level')}</Label>
                        <AvField
                            id="confidentialityLevel"
                            name="confidentialityLevel"
                            type="select"
                            errorMessage={t("This field cannot be blank")}
                            className="form-control"
                            validate={{
                                required: { value: true }
                            }}
                        >
                            <option value="">-- {t('Select Level')} --</option>

                            {DocumentsConfidentialityLevels.map((level) => {
                                return (
                                    <option value={level.value}>
                                        {t(level.title)}
                                    </option>
                                )
                            })}
                        </AvField>
                    </AvGroup>
                </ModalBody>
                <ModalFooter>
                    <Button 
                        color="primary" 
                        type="submit"
                        disabled={addingAttachmentInProcess}
                    >
                        {addingAttachmentInProcess && (
                            <Spinner 
                                color='white'
                                size='sm'
                                className='me-1'
                            />
                        )}
                        {t('Upload')}
                    </Button>

                    <Button 
                        color="secondary" 
                        onClick={toggle}
                        disabled={addingAttachmentInProcess}
                    >
                        {t('Cancel')}
                    </Button>
                </ModalFooter>
            </AvForm>
        </Modal>
    );
};

export default withNamespaces()(
    AddAttachmentModal
);